import PropTypes from "prop-types"
import React, { useCallback, useState } from "react"

import LoginForgotPasswordModal from "src/components/LoginForgotPasswordModal"
import ProfileImage from "src/components/ProfileImage"

import { fetchCurrentUser } from "src/api/users"

import usePopupAuth from "src/hooks/use_popup_auth"

import { queryApi } from "src/utils/api/ApiMethods"

// Mirrors the Rails-side `insurance_complete?` / `registration_complete?` so
// BoaterForm can gate its insurance/registration sections on the selected
// boat's completeness regardless of which source the boat shape came from.
const insuranceComplete = (insurance) =>
  Boolean(
    insurance &&
      insurance.company_name &&
      insurance.policy_number &&
      insurance.expiration_date
  )

const registrationComplete = (registration) =>
  Boolean(registration && registration.number && registration.expiration_date)

const normalizeBoats = (boats) =>
  (boats || []).map((boat) => ({
    ...boat,
    hasInsurance: insuranceComplete(boat.insurance),
    hasRegistration: registrationComplete(boat.registration),
  }))

const SignInSignOutToggle = ({ currentUser, onAuthSuccess }) => {
  const [error, setError] = useState(null)
  const [legacyModalOpen, setLegacyModalOpen] = useState(false)

  const handleSuccess = useCallback(async () => {
    setError(null)
    try {
      const [user, boats] = await Promise.all([
        fetchCurrentUser(),
        queryApi("/account/boats"),
      ])
      onAuthSuccess(user, normalizeBoats(boats))
    } catch {
      setError(
        "Signed in, but we couldn't refresh your profile. Reload to continue."
      )
    }
  }, [onAuthSuccess])

  const openSignIn = usePopupAuth({
    onSuccess: handleSuccess,
    onError: setError,
  })

  if (currentUser) {
    const { name, email } = currentUser
    return (
      <div className="flex content-center items-center gap-3 rounded-xl border border-gray-400 p-3 md:w-max">
        <ProfileImage name={name} />
        <div className="flex flex-col gap-1">
          <span className="text-base text-gray-800">
            You are signed in as {name}.{" "}
            <a
              className="text-sm font-semibold text-blue-600"
              href="/users/sign_out?redirect_back=true"
            >
              Not you?
            </a>
          </span>
          <span className="text-gray-600">{email}</span>
        </div>
      </div>
    )
  }

  // WORKOS_AUTH_ENABLED off: fall back to the pre-WorkOS inline modal that
  // posts to Devise's /users/sign_in. Reloads on success to pick up
  // current_user from the Rails-rendered props on the next render.
  if (!window.workosAuthEnabled) {
    return (
      <>
        <div>
          Have a Dockwa account?{" "}
          <span
            onClick={() => setLegacyModalOpen(true)}
            className="cursor-pointer font-semibold text-blue-600"
          >
            Log in
          </span>
        </div>
        {legacyModalOpen && (
          <LoginForgotPasswordModal
            closeModal={() => setLegacyModalOpen(false)}
          />
        )}
      </>
    )
  }

  return (
    <>
      <div>
        Have a Dockwa account?{" "}
        <span
          onClick={() => openSignIn(window.location.pathname)}
          className="cursor-pointer font-semibold text-blue-600"
        >
          Log in
        </span>
      </div>
      {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
    </>
  )
}

SignInSignOutToggle.propTypes = {
  currentUser: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }),
  onAuthSuccess: PropTypes.func.isRequired,
}

export default SignInSignOutToggle
